import { useCallback } from 'react';
import type { UIAnalyticsEvent } from '@atlaskit/analytics-next';
import { ClientSideValidationError } from '@atlassian/jira-business-error-handling';
import { useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import type {
	ClipboardItem,
	ListIssueFieldTransformed,
	ListIssueForCopyPaste,
	PasteMetadata,
} from '../types';
import { VALIDATION_ERROR_CODES } from './constants';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type AnyPromise = Promise<any>;

export function useClipboardHandler<T>({
	issue,
	field,
	currentStringValue,
	currentValue,
	getPastedValue,
	saveValue,
	emptyValue,
}: {
	issue: ListIssueForCopyPaste;
	field: ListIssueFieldTransformed;
	currentStringValue: string | null;
	currentValue: T | null;
	getPastedValue: (clipboardItem: ClipboardItem, metadata: PasteMetadata | null) => T | null;
	saveValue: (value: T | null, meta: null, analyticsEvent: UIAnalyticsEvent) => AnyPromise;
	emptyValue?: T;
}) {
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const copyHandler = useCallback(
		() => getFieldValueAsClipboardItem<T>(field, issue, currentStringValue, currentValue),
		[currentStringValue, currentValue, field, issue],
	);

	const pasteHandler = useCallback(
		async (clipboardItem: ClipboardItem, metadata: PasteMetadata | null) => {
			const value = getPastedValue(clipboardItem, metadata);

			if (!value || (Array.isArray(value) && !value.length)) {
				return false;
			}

			await saveValue(value, null, createAnalyticsEvent({}));

			return true;
		},
		[getPastedValue, saveValue, createAnalyticsEvent],
	);

	const cutHandler = useCallback(async () => {
		try {
			await saveValue(emptyValue ?? null, null, createAnalyticsEvent({}));

			return true;
		} catch (e) {
			return false;
		}
	}, [saveValue, emptyValue, createAnalyticsEvent]);

	return {
		copyHandler,
		pasteHandler,
		cutHandler,
	};
}
/**
 * To be deleted when all the fields are moved to useClipboardHandler
 * @deprecated use useClipboardHandler instead
 */
export function useGenericClipboardHandler<T>(
	field: ListIssueFieldTransformed,
	getValue: (clipboardItem: ClipboardItem, metadata: PasteMetadata | null) => T | null,
	saveValue: (value: T | null, meta: null, analyticsEvent: UIAnalyticsEvent) => AnyPromise,
	emptyValue?: T,
) {
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const pasteHandler = useCallback(
		async (clipboardItem: ClipboardItem, metadata: PasteMetadata | null) => {
			const value = getValue(clipboardItem, metadata);

			if (!value || (Array.isArray(value) && !value.length)) {
				return false;
			}

			await saveValue(value, null, createAnalyticsEvent({}));

			return true;
		},
		[getValue, saveValue, createAnalyticsEvent],
	);

	const cutHandler = useCallback(async () => {
		try {
			await saveValue(emptyValue ?? null, null, createAnalyticsEvent({}));

			return true;
		} catch (e) {
			return false;
		}
	}, [saveValue, emptyValue, createAnalyticsEvent]);

	return {
		copyHandler: null,
		pasteHandler,
		cutHandler,
	};
}

export function assertClipboardHasPlainTextFormat(
	clipboardItem: ClipboardItem | undefined,
): clipboardItem is { plainText: string } {
	if (!clipboardItem?.plainText) {
		throw new ClientSideValidationError(VALIDATION_ERROR_CODES.EMPTY_CLIPBOARD);
	}

	return true;
}

export function assertClipboardHasRichFormat(
	clipboardItem: ClipboardItem | undefined,
): clipboardItem is { plainText: string; html: string } {
	assertClipboardHasPlainTextFormat(clipboardItem);
	if (!clipboardItem?.html) {
		throw new ClientSideValidationError(VALIDATION_ERROR_CODES.NON_COMPATIBLE_FIELD_TYPE);
	}

	return true;
}

export function assertClipboardDataIsCompatible(
	metadata: PasteMetadata | null,
	compatibleTypes: string[],
): metadata is PasteMetadata {
	if (!metadata?.fieldType || !compatibleTypes.includes(metadata?.fieldType)) {
		throw new ClientSideValidationError(VALIDATION_ERROR_CODES.NON_COMPATIBLE_FIELD_TYPE);
	}

	return true;
}

function getStringifiedRichFieldValue<T>(richValue: T | null) {
	if (!richValue) {
		return null;
	}

	if (typeof richValue === 'string') {
		return { value: richValue };
	}

	return richValue;
}

const safelyAddRichValueToHtml = (
	html: string,
	richValue: ReturnType<typeof getStringifiedRichFieldValue>,
) => {
	try {
		const DOM = new DOMParser().parseFromString(html, 'text/html');
		DOM.body?.querySelector('div')?.setAttribute('data-field-value', JSON.stringify(richValue));
		return DOM.body?.innerHTML || '';
	} catch (e) {
		return html;
	}
};

export function getFieldValueAsClipboardItem<T>(
	field: ListIssueFieldTransformed,
	issue: {
		key?: string;
		issueId?: string;
		issueType?: {
			level?: number;
		};
	},
	textValue: string | null,
	value: T | null,
): ClipboardItem {
	const richValue = getStringifiedRichFieldValue(value);

	if (!textValue) {
		return {};
	}

	const html = `<div data-type="list-cell" data-issue-type-level="${issue?.issueType?.level}" data-field-id="${field.fieldId}" data-field-type="${field.type}">${textValue}</div>`;

	return {
		html: richValue ? safelyAddRichValueToHtml(html, richValue) : html,
		plainText: textValue,
	};
}
